import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { firstValueFrom, of } from 'rxjs';
import {
  filter,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import { AssetAllocatedTableService } from '../../asset-management-table/asset-allocated-table.service';
import { FileUploadService } from '@app/shared/services/file-upload.service';
import { AuthService } from '../../../../core/auth.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetManagementService } from '../../asset-management-service';
import { ToasterService } from '@app/shared/services/toaster.service';

export interface AddNewAssetDialogData {
  row?: any;
  mode?: 'add' | 'replace';
}

@Component({
  selector: 'app-add-new-asset-form',
  templateUrl: './add-new-asset-form.component.html',
  styleUrls: ['./add-new-asset-form.component.scss'],
})
export class AddNewAssetFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;

  readonly actionOptions = [
    { label: 'Add', value: 'add' },
    { label: 'Replace', value: 'replace' },
  ];

  readonly laptopOptions = [
    { label: 'MacBook', value: 'MacBook' },
    { label: 'Windows', value: 'Windows' },
  ];

  accessoriesTypeOptions: { label: string; value: string }[] = [];
  private readonly fallbackAccessoriesOptions = [
    { label: 'Mouse', value: 'Mouse' },
    { label: 'Keyboard', value: 'Keyboard' },
    { label: 'Dock', value: 'Dock' },
    { label: 'Headset', value: 'Headset' },
    { label: 'Monitor', value: 'Monitor' },
    { label: 'Other', value: 'Other' },
  ];

  isLoadingAccessories = false;

  attachedFiles: File[] = [];
  uploadedFileUrl: string = '';
  attachmentLabel: string = 'Attachment';

  // Email search properties
  emailSearchControl = new FormControl('');
  emailOptions: any[] = [];
  showEmployeeSearch = false;
  showEmployeeDetails = false;
  emailNotFoundError: string | null = null;
  employeeDataMap: Map<string, any> = new Map();
  private selectedEmployeeEmail: string | null = null;

  // Replace email search properties (separate from main email search)
  replaceEmailSearchControl = new FormControl('');
  replaceEmailOptions: any[] = [];
  replaceEmailNotFoundError: string | null = null;
  replaceEmployeeDataMap: Map<string, any> = new Map();
  private selectedReplaceEmployeeEmail: string | null = null;
  @ViewChild('replaceEmailAutoInput')
  replaceEmailAutoInput!: ElementRef<HTMLInputElement>;

  // CC functionality properties
  toEmails: string[] = [];
  ccEmails: string[] = [];
  duplicateEmailError: string | null = null;
  @ViewChild('emailAutoInput') emailAutoInput!: ElementRef<HTMLInputElement>;

  // Dropdown options for cascading dropdowns
  osTypeOptions: { label: string; value: string }[] = [];
  brandNameOptions: { label: string; value: string }[] = [];
  laptopTypeOptions: { label: string; value: string }[] = [];

  constructor(
    private readonly dialogRef: MatDialogRef<AddNewAssetFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fb: FormBuilder,
    private readonly assetAllocatedService: AssetAllocatedTableService,
    private readonly fileUploadService: FileUploadService,
    private readonly authService: AuthService,
    private readonly assetManagementService: AssetManagementService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toasterService: ToasterService,
  ) {
    // Constructor only for dependency injection
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscriptions();
    this.setupInitialFormState();
    this.loadAccessoriesFromCache();
    this.updateAttachmentLabel();
    this.setupDebouncedEmailSearch();
    this.setupDebouncedReplaceEmailSearch();

    // Remove automatic initialization - only initialize for replace action
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private initializeForm(): void {
    const row =
      this.data && (this.data as any).row
        ? (this.data as any).row
        : this.data && (this.data as any).data
          ? (this.data as any).data
          : (this.data as any) || {};

    const defaultActionValue = this.data?.mode || 'add';
    const defaultAction =
      this.actionOptions.find((opt) => opt.value === defaultActionValue) ||
      this.actionOptions[0];
    // Process accessories data from incoming row
    let initialAccessories: string[] = [];
    if (row.accessoriesType && Array.isArray(row.accessoriesType)) {
      // Handle array like ['USB Mouse, Headset, Laptop']
      initialAccessories = row.accessoriesType
        .flatMap((item: string) => item.split(','))
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    } else if (row.accessoriesType && typeof row.accessoriesType === 'string') {
      // Handle single comma-separated string
      initialAccessories = row.accessoriesType
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    }

    this.form = this.fb.group({
      action: [defaultAction, [Validators.required]],
      email: [row.email || '', []],
      employeeId: [row.employeeId || '', []],
      fullName: [row.fullName || row.name || '', []],
      labelName: [row.labelName || row.fullName || row.name || '', []],
      emailId: [row.emailId || row.email || '', []],
      laptop: [row.laptopType || row.laptop || '', [Validators.required]],
      laptopSerialNumber: [
        row.laptopSerialNumber || '',
        [Validators.required, Validators.maxLength(120)],
      ],
      chargerSerialNumber: [
        row.chargerSerialNumber || '',
        [Validators.required, Validators.maxLength(120)],
      ],
      accessoriesType: [
        initialAccessories,
        defaultActionValue === 'add' ? [Validators.required] : [],
      ],
      dateOfPickup: [row.dateOfPickup ? new Date(row.dateOfPickup) : null],
      comments: [row.comments || '', [Validators.maxLength(1000)]],
      attachments: [null],
      // Add cascading dropdown form controls
      osType: [row.osType || '', []],
      brandName: [row.brandName || '', []],
      laptopType: [row.laptopType || '', []],
    });
  }

  private setupFormSubscriptions(): void {
    this.form.get('action')?.valueChanges.subscribe((action) => {
      this.handleActionChange(action);
    });

    this.form.get('laptop')?.valueChanges.subscribe(() => {
      this.updateAttachmentLabel();
    });
  }

  private setupInitialFormState(): void {
    const currentAction = this.form.get('action')?.value;

    // Determine if we need to show employee search
    const hasEmployeeId =
      this.data?.data?.employeeId ||
      (this.data as any)?.employeeId ||
      (this.data?.data && this.data.data.employeeId);
    const isReplaceAction =
      currentAction?.value === 'replace' || currentAction === 'replace';

    // Show employee search for Add action without employee OR for Replace action
    this.showEmployeeSearch = !hasEmployeeId || isReplaceAction;
    this.showEmployeeDetails = !!hasEmployeeId && !isReplaceAction;

    // Set email as required with email validator if showing employee search
    if (this.showEmployeeSearch) {
      this.form
        .get('email')
        ?.setValidators([Validators.required, Validators.email]);
      this.form.get('email')?.updateValueAndValidity();
    }

    if (currentAction?.value === 'add' || currentAction === 'add') {
      this.configureFormForAddAction();
    } else {
      this.configureFormForReplaceAction();
    }
  }

  private handleActionChange(action: any): void {
    const accessoriesTypeControl = this.form.get('accessoriesType');
    const isReplaceAction = action?.value === 'replace' || action === 'replace';

    // Clear TO/CC emails when switching away from replace action
    if (!isReplaceAction) {
      this.toEmails = [];
      this.ccEmails = [];
      this.duplicateEmailError = null;

      // Clear cascading dropdown fields and remove validators
      this.form.patchValue({
        osType: '',
        brandName: '',
        laptopType: '',
      });
      this.form.get('osType')?.clearValidators();
      this.form.get('brandName')?.clearValidators();
      this.form.get('laptopType')?.clearValidators();

      // Clear dropdown options
      this.osTypeOptions = [];
      this.brandNameOptions = [];
      this.laptopTypeOptions = [];
    } else {
      // Add validators for replace action
      this.form.get('osType')?.setValidators([Validators.required]);
      this.form.get('brandName')?.setValidators([Validators.required]);
      this.form.get('laptopType')?.setValidators([Validators.required]);

      // Initialize dropdown options
      this.fetchAccessoriesDetails('', '', 1);

      // Load email notifications when switching to replace action
      const userProfile = sessionStorage.getItem('userProfile');
      let city = 'Hyderabad'; // Default fallback
      if (userProfile) {
        try {
          const profileData = JSON.parse(userProfile);
          city = profileData?.city || 'Hyderabad';
        } catch (error) {
          console.error(
            'Error parsing userProfile from session storage:',
            error,
          );
        }
      }
    }

    // Update validators
    this.form.get('osType')?.updateValueAndValidity();
    this.form.get('brandName')?.updateValueAndValidity();
    this.form.get('laptopType')?.updateValueAndValidity();

    // Update email search visibility based on action
    const hasEmployeeId =
      this.data?.data?.employeeId ||
      (this.data as any)?.employeeId ||
      (this.data?.data && this.data.data.employeeId);
    this.showEmployeeSearch = !hasEmployeeId || isReplaceAction;
    this.showEmployeeDetails = !!hasEmployeeId && !isReplaceAction;

    // Set email validation if showing employee search
    if (this.showEmployeeSearch) {
      this.form
        .get('email')
        ?.setValidators([Validators.required, Validators.email]);
      this.form.get('email')?.updateValueAndValidity();
    } else {
      this.form.get('email')?.clearValidators();
      this.form.get('email')?.updateValueAndValidity();
    }

    if (action?.value === 'add' || action === 'add') {
      this.configureFormForAddAction();
      accessoriesTypeControl?.setValidators([Validators.required]);
      accessoriesTypeControl?.updateValueAndValidity();
    } else {
      this.configureFormForReplaceAction();
      accessoriesTypeControl?.clearValidators();
      accessoriesTypeControl?.updateValueAndValidity();
    }

    this.updateAttachmentLabel();
  }

  private configureFormForAddAction(): void {
    const laptopControl = this.form.get('laptop');
    const laptopSerialControl = this.form.get('laptopSerialNumber');
    const chargerSerialControl = this.form.get('chargerSerialNumber');
    const dateOfPickupControl = this.form.get('dateOfPickup');

    // For add action, laptop is not required, serial numbers are not required
    laptopControl?.clearValidators();
    laptopSerialControl?.clearValidators();
    chargerSerialControl?.clearValidators();
    laptopControl?.updateValueAndValidity();
    laptopSerialControl?.updateValueAndValidity();
    chargerSerialControl?.updateValueAndValidity();
    dateOfPickupControl?.clearValidators();
    dateOfPickupControl?.updateValueAndValidity();
    laptopControl?.enable(); // Enable laptop selection for add action
    laptopSerialControl?.disable();
    chargerSerialControl?.disable();
    laptopControl?.reset();
  }

  private configureFormForReplaceAction(): void {
    const laptopControl = this.form.get('laptop');
    const laptopSerialControl = this.form.get('laptopSerialNumber');
    const chargerSerialControl = this.form.get('chargerSerialNumber');
    const dateOfPickupControl = this.form.get('dateOfPickup');

    // For replace action, laptop field is not required (using cascading dropdowns instead)
    laptopControl?.clearValidators();
    laptopSerialControl?.setValidators([
      Validators.required,
      Validators.maxLength(120),
    ]);
    chargerSerialControl?.setValidators([
      Validators.required,
      Validators.maxLength(120),
    ]);
    dateOfPickupControl?.setValidators([Validators.required]);
    laptopControl?.updateValueAndValidity();
    laptopSerialControl?.updateValueAndValidity();
    chargerSerialControl?.updateValueAndValidity();
    dateOfPickupControl?.updateValueAndValidity();
    laptopControl?.disable(); // Disable since we're using cascading dropdowns
    laptopSerialControl?.enable();
    chargerSerialControl?.enable();
  }

  private loadAccessoriesFromCache(): void {
    if (this.assetManagementService.isAccessoriesLoaded()) {
      this.accessoriesTypeOptions =
        this.assetManagementService.getAccessoriesCache();
      this.afterAccessoriesLoaded();
    } else {
      this.loadAccessoriesFromAPI();
    }
  }

  private async loadAccessoriesFromAPI(): Promise<void> {
    this.isLoadingAccessories = true;
    try {
      const response: any = await firstValueFrom(
        this.assetManagementService.getAllAccessoriesAfterUpdate(),
      );

      if (Array.isArray(response?.data) && response.data.length > 0) {
        this.accessoriesTypeOptions = response.data.map((item: any) => ({
          label: item?.accessoriesType ?? '',
          value: item?.accessoriesType ?? '',
        }));
      } else {
        this.accessoriesTypeOptions = this.fallbackAccessoriesOptions;
      }
    } catch (error) {
      this.accessoriesTypeOptions = this.fallbackAccessoriesOptions;
    } finally {
      this.isLoadingAccessories = false;
      this.afterAccessoriesLoaded();
    }
  }

  private afterAccessoriesLoaded(): void {
    setTimeout(() => {
      const currentAccessories = this.form.get('accessoriesType')?.value;

      if (
        currentAccessories &&
        Array.isArray(currentAccessories) &&
        currentAccessories.length > 0
      ) {
        // Validate accessories against available options
        const validAccessories = currentAccessories.filter((accessory) =>
          this.accessoriesTypeOptions.some(
            (option) => option.value === accessory,
          ),
        );

        if (validAccessories.length > 0) {
          // Force set the valid accessories
          this.form.get('accessoriesType')?.setValue(validAccessories);
        }
      }
    }, 100);
  }

  handleFileInputChange(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.attachedFiles = Array.from(input.files);
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);
  }

  onAttachmentSelected(file: File): void {
    this.attachedFiles = [file];

    this.fileUploadService.uploadFile(file, 'assetUpload').subscribe({
      next: (resp) => {
        if (resp?.success && resp?.filePath) {
          this.uploadedFileUrl = resp.filePath;
          this.form.get('attachments')?.setErrors(null);
          this.form.get('attachments')?.markAsUntouched();
        } else {
          this.uploadedFileUrl = '';
        }
      },
      error: (err) => {
        this.uploadedFileUrl = '';
      },
    });
  }

  onAttachmentRemoved(): void {
    this.attachedFiles = [];
    this.uploadedFileUrl = '';
    if (this.isReplaceActionWithLaptop()) {
      this.form.get('attachments')?.setErrors({ required: true });
      this.form.get('attachments')?.markAsTouched();
    }
  }

  /**
   * Builds action-specific payload fields based on whether it's Add or Replace action
   * @param actionValue - The normalized action value ('add' or 'replace')
   * @param formValue - The form values
   * @returns Object with action-specific fields
   */
  private buildActionSpecificPayload(actionValue: string, formValue: any): any {
    if (actionValue === 'replace') {
      // For replace action, use cascading dropdown fields and email arrays
      return {
        osType: formValue.osType || '',
        brandName: formValue.brandName || '',
        laptopType: formValue.laptopType || '',
        dateOfPickup: this.formatDate(formValue.dateOfPickup),
        toEmailIds: this.toEmails || [],
        ccEmailIds: this.ccEmails || [],
      };
    } else {
      // For add action, use regular laptop field
      return {
        laptopType: formValue.laptop,
      };
    }
  }

  submit(): void {
    const value = this.form.getRawValue();

    const actionValue = value.action?.value || value.action;
    const isReplaceAction = actionValue === 'replace';

    // Attachment validation logic based on action type
    if (isReplaceAction) {
      // For replace action, check if any cascading dropdown laptop type is selected
      const hasLaptopTypeSelected =
        value.laptopType && value.laptopType.trim() !== '';
      if (
        hasLaptopTypeSelected &&
        !this.uploadedFileUrl &&
        this.attachedFiles.length === 0
      ) {
        this.form.get('attachments')?.setErrors({ required: true });
        this.form.get('attachments')?.markAsTouched();
        return;
      }
    } else {
      // For add action, use the regular laptop field validation
      const laptopValue = value.laptop;
      const hasLaptopSelected =
        laptopValue && (laptopValue === 'MacBook' || laptopValue === 'Windows');
      if (
        hasLaptopSelected &&
        !this.uploadedFileUrl &&
        this.attachedFiles.length === 0
      ) {
        this.form.get('attachments')?.setErrors({ required: true });
        this.form.get('attachments')?.markAsTouched();
        return;
      }
    }
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control && control.invalid) {
        }
      });
      this.form.markAllAsTouched();
      return;
    }

    let normalizedActionValue = '';
    if (typeof value.action === 'string') {
      normalizedActionValue = value.action;
    } else if (
      value.action &&
      typeof value.action === 'object' &&
      value.action.value
    ) {
      normalizedActionValue = value.action.value;
    } else {
      normalizedActionValue = 'add';
    }

    const normalizedAction =
      normalizedActionValue === 'replace' ? 'Replace' : 'Add';

    const accessoriesValue: string[] = Array.isArray(value.accessoriesType)
      ? value.accessoriesType
      : value.accessoriesType
        ? [value.accessoriesType]
        : [];

    let fileUrlValue = '';
    if (this.uploadedFileUrl) {
      fileUrlValue = this.uploadedFileUrl;
    } else if (this.attachedFiles.length > 0) {
      fileUrlValue = this.attachedFiles[0].name;
    }

    // Get employeeId from form (either from selected row or from email search)
    const employeeId =
      value.employeeId ||
      this.data?.data?.employeeId ||
      (this.data as any)?.employeeId ||
      '';

    if (!employeeId || !employeeId.trim()) {
      this.form.get('email')?.setErrors({ required: true });
      this.form.get('email')?.markAsTouched();
      return;
    }

    const rawPayloadSource = {
      assetAction: normalizedAction,
      ...this.buildActionSpecificPayload(normalizedActionValue, value),
      accessoriesType: accessoriesValue,
      comments: value.comments || '',
      fileUrl: fileUrlValue,
      laptopSerialNumber: value.laptopSerialNumber || '',
      chargerSerialNumber: value.chargerSerialNumber || '',
      employeeId: employeeId.trim(),
      emailId: value.emailId || value.email || '',
      labelName: 'Assets Allocated',
    };

    this.assetManagementService.createAssetReplace(rawPayloadSource).subscribe({
      next: (resp: any) => {
        // Show success toaster based on action type
        const defaultMessage = isReplaceAction 
          ? 'Asset replaced successfully!' 
          : 'Asset added successfully!';
      
        this.toasterService.showSuccess(defaultMessage, 'Success');
        
        this.dialogRef.close({ submitted: true, apiResponse: resp });
      },
      error: (err: any) => {
        // Show error toaster
        const errorMessage = err?.error?.message || err?.message || 'Failed to submit asset request. Please try again.';
        this.toasterService.showError(errorMessage, 'Error');
      },
    });
  }

  cancel(): void {
    this.dialogRef.close({ submitted: false });
  }

  hasError(control: string, error: string): boolean {
    const c = this.form.get(control);
    return !!c && c.touched && c.hasError(error);
  }

  isFormValid(): boolean {
    const formValue = this.form.getRawValue();
    // Check basic form validity
    if (!this.form.valid) {
      return false;
    }

    // Additional laptop attachment validation based on action type
    const actionValue = formValue.action?.value || formValue.action;
    const isReplaceAction = actionValue === 'replace';

    if (isReplaceAction) {
      // For replace action, check cascading dropdown laptop type
      const hasLaptopTypeSelected =
        formValue.laptopType && formValue.laptopType.trim() !== '';
      if (
        hasLaptopTypeSelected &&
        !this.uploadedFileUrl &&
        this.attachedFiles.length === 0
      ) {
        return false;
      }
    } else {
      // For add action, check regular laptop field
      const laptopValue = formValue.laptop;
      const hasLaptopSelected =
        laptopValue && (laptopValue === 'MacBook' || laptopValue === 'Windows');
      if (
        hasLaptopSelected &&
        !this.uploadedFileUrl &&
        this.attachedFiles.length === 0
      ) {
        return false;
      }
    }

    return true;
  }

  onAccessoriesChange(selectedValues: any): void {
    if (Array.isArray(selectedValues)) {
      const values = selectedValues.map((item) =>
        typeof item === 'string' ? item : item.value || item,
      );
      this.form.patchValue({ accessoriesType: values });
    } else if (selectedValues && selectedValues.length !== undefined) {
      const values = Array.from(selectedValues).map((item: any) =>
        typeof item === 'string' ? item : item.value || item,
      );
      this.form.patchValue({ accessoriesType: values });
    }
  }

  getAccessoriesTypeLabel(): string {
    return this.isActionAdd() ? 'Accessories Type*' : 'Accessories Type';
  }

  getAttachmentLabel(): string {
    return this.attachmentLabel;
  }

  updateAttachmentLabel(): void {
    this.attachmentLabel = this.isReplaceActionWithLaptop()
      ? 'Attachment*'
      : 'Attachment';
  }

  isReplaceActionWithLaptop(): boolean {
    const actionValue = this.form?.get('action')?.value;
    const isReplaceAction =
      actionValue?.value === 'replace' || actionValue === 'replace';

    if (!isReplaceAction) {
      return false;
    }

    // For replace action, check if cascading dropdown laptop type is selected
    const laptopTypeValue = this.form?.get('laptopType')?.value;
    const hasLaptopTypeSelected =
      laptopTypeValue && laptopTypeValue.trim() !== '';
    return hasLaptopTypeSelected;
  }

  isActionAdd(): boolean {
    const actionValue = this.form?.get('action')?.value;
    return actionValue?.value === 'add' || actionValue === 'add';
  }

  private setupDebouncedEmailSearch(): void {
    this.emailSearchControl.valueChanges
      .pipe(
        filter((val) => typeof val === 'string' && val !== null),
        debounceTime(300), // Faster debounce like my-request form
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          if (searchTerm.trim().length < 2) {
            return of({ data: [] });
          }
          return this.assetManagementService.searchEmployeesByEmail(
            searchTerm.trim(),
          );
        }),
      )
      .subscribe({
        next: (response: any) => {
          const employees = response?.data || response || [];
          this.employeeDataMap.clear();
          this.emailOptions = employees?.map((employee: any) => {
            this.employeeDataMap.set(employee.email, employee);
            return {
              label: employee.email,
              value: employee.email,
            };
          });

          // Clear error when we get results
          if (this.emailOptions.length > 0) {
            this.emailNotFoundError = null;
          }
        },
        error: () => {
          this.emailOptions = [];
          this.emailNotFoundError =
            'Error searching for employees. Please try again.';
        },
      });
  }

  displayEmailFn(value: any): string {
    return value?.label || value?.value || value || '';
  }

  onEmployeeEmailOptionSelected(event: any): void {
    this.selectedEmployeeEmail = event.option?.value || null;
  }

  onEmployeeEmailButtonClick(): void {
    const emailToAdd = this.selectedEmployeeEmail;
    if (!emailToAdd) {
      this.duplicateEmailError = null;
      return;
    }
    if (
      this.toEmails.includes(emailToAdd) ||
      this.ccEmails.includes(emailToAdd)
    ) {
      this.duplicateEmailError = 'This email is already added to TO or CC.';
    } else {
      this.toEmails.push(emailToAdd);
      this.duplicateEmailError = null;
    }
    this.selectedEmployeeEmail = null;
    if (this.emailAutoInput) {
      this.emailAutoInput.nativeElement.value = '';
    }
    this.emailSearchControl.setValue('');
  }

  /**
   * Adds manager emails from employee data, fetching from AuthService if needed
   * @param employee - Employee data from search results
   */

  /**
   * Automatically adds reporting manager and DNT leader emails to CC list
   * @param employee - Employee data containing manager email IDs
   */
  private addManagerEmailsToCC(employee: any): void {
    const emailsToAdd: string[] = [];

    // Add reporting manager email if available
    if (
      employee.reportingManagerEmailId &&
      typeof employee.reportingManagerEmailId === 'string'
    ) {
      const managerEmail = employee.reportingManagerEmailId.trim();

      if (
        managerEmail &&
        !this.ccEmails.includes(managerEmail) &&
        !this.toEmails.includes(managerEmail)
      ) {
        emailsToAdd.push(managerEmail);
      }
    }

    // Add DNT leader email if available
    if (
      employee.dntLeaderEmailId &&
      typeof employee.dntLeaderEmailId === 'string'
    ) {
      const dntLeaderEmail = employee.dntLeaderEmailId.trim();

      if (
        dntLeaderEmail &&
        !this.ccEmails.includes(dntLeaderEmail) &&
        !this.toEmails.includes(dntLeaderEmail)
      ) {
        emailsToAdd.push(dntLeaderEmail);
      }
    }

    // Add the emails to CC list
    if (emailsToAdd.length > 0) {
      this.ccEmails.push(...emailsToAdd);
    }
  }

  onEmployeeEmailCcButtonClick(): void {
    const emailToAdd = this.selectedEmployeeEmail;
    if (!emailToAdd) {
      this.duplicateEmailError = null;
      return;
    }
    if (
      this.ccEmails.includes(emailToAdd) ||
      this.toEmails.includes(emailToAdd)
    ) {
      this.duplicateEmailError = 'This email is already added to TO or CC.';
    } else {
      this.ccEmails.push(emailToAdd);
      this.duplicateEmailError = null;
    }
    this.selectedEmployeeEmail = null;
    if (this.emailAutoInput) {
      this.emailAutoInput.nativeElement.value = '';
    }
    this.emailSearchControl.setValue('');
  }

  // Separate replace email search methods
  private setupDebouncedReplaceEmailSearch(): void {
    this.replaceEmailSearchControl.valueChanges
      .pipe(
        filter((val) => typeof val === 'string' && val !== null),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          if (searchTerm.trim().length < 2) {
            return of({ data: [] });
          }
          return this.assetManagementService.searchEmployeesByEmail(
            searchTerm.trim(),
          );
        }),
      )
      .subscribe({
        next: (response: any) => {
          const employees = response?.data || response || [];
          this.replaceEmployeeDataMap.clear();
          this.replaceEmailOptions = employees?.map((employee: any) => {
            this.replaceEmployeeDataMap.set(employee.email, employee);
            return {
              label: employee.email,
              value: employee.email,
            };
          });
          this.cdr.markForCheck();
        },
        error: () => {
          this.replaceEmailOptions = [];
          this.cdr.markForCheck();
        },
      });
  }

  onReplaceEmployeeEmailOptionSelected(event: any): void {
    this.selectedReplaceEmployeeEmail = event.option?.value || null;
    const employee = this.replaceEmployeeDataMap.get(
      this.selectedReplaceEmployeeEmail || '',
    );

    if (employee) {
      // Patch form values for replace action - resource email sets both emailId and employeeId
      this.form.patchValue({
        email: employee.email || employee.emailId || '',
        employeeId: employee.employeeId || employee.psId || '',
        fullName: employee.fullName || employee.name || '',
        labelName: employee.fullName || employee.name || '',
        emailId:
          this.selectedReplaceEmployeeEmail ||
          employee.email ||
          employee.emailId ||
          '',
      });

      // Fetch complete employee data to get city and load email notifications
      this.loadEmailNotificationsForSelectedEmployee(
        this.selectedReplaceEmployeeEmail,
      );

      this.replaceEmailNotFoundError = null;
    } else {
      this.replaceEmailNotFoundError = 'Employee not found.';
    }
  }

  onReplaceEmployeeEmailButtonClick(): void {
    const emailToAdd = this.selectedReplaceEmployeeEmail;
    if (!emailToAdd) {
      this.duplicateEmailError = null;
      return;
    }
    if (
      this.toEmails.includes(emailToAdd) ||
      this.ccEmails.includes(emailToAdd)
    ) {
      this.duplicateEmailError = 'This email is already added to TO or CC.';
    } else {
      this.toEmails.push(emailToAdd);
      this.duplicateEmailError = null;
    }
    this.selectedReplaceEmployeeEmail = null;
    if (this.replaceEmailAutoInput) {
      this.replaceEmailAutoInput.nativeElement.value = '';
    }
    this.replaceEmailSearchControl.setValue('');
  }

  /**
   * Loads email notifications for selected employee by fetching complete data and using their city
   * @param employeeEmail - The email of the selected employee
   */
  private async loadEmailNotificationsForSelectedEmployee(
    employeeEmail: string | null,
  ): Promise<void> {
    if (!employeeEmail) {
      return;
    }
    try {
      const employeeResponse = await this.authService
        .getEmployeeData(employeeEmail)
        .toPromise();
      if (employeeResponse?.data) {
        const employeeData = employeeResponse.data;
        const city = employeeData.city || 'Hyderabad';
        this.loadEmailNotifications(city);
        this.addManagerEmailsToCC(employeeData);
      } else {
        this.loadEmailNotifications('Hyderabad');
      }
    } catch (error) {
      this.loadEmailNotifications('Hyderabad');
    }
  }

  removeToEmail(index: number): void {
    if (index >= 0 && index < this.toEmails.length) {
      this.toEmails.splice(index, 1);
    }
  }

  removeCcEmail(index: number): void {
    if (index >= 0 && index < this.ccEmails.length) {
      this.ccEmails.splice(index, 1);
    }
  }

  loadEmailNotifications(location: string): void {
    this.assetManagementService.getEmailNotifications(location).subscribe({
      next: (response) => {
        const existingCcEmails = [...this.ccEmails];
        const existingToEmails = [...this.toEmails];
        const locationToEmails = response?.to || [];
        const locationCcEmails = response?.cc || [];
        this.toEmails = [
          ...new Set([...existingToEmails, ...locationToEmails]),
        ];
        this.ccEmails = [
          ...new Set([...existingCcEmails, ...locationCcEmails]),
        ];
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.cdr.markForCheck();
      },
    });
  }

  async fetchAccessoriesDetails(
    osType: string = '',
    brandName: string = '',
    accessoriesTypeId: number = 1,
  ): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.assetManagementService.getAccessoriesDetails(
          osType,
          brandName,
          accessoriesTypeId,
        ),
      );

      let dataArray: any[] = [];
      if (Array.isArray(response?.data)) {
        dataArray = response.data;
      } else if (Array.isArray(response)) {
        dataArray = response;
      }

      if (dataArray.length > 0) {
        const mappedOptions = dataArray.map((item: string) => ({
          label: item,
          value: item,
        }));

        if (!osType && !brandName) {
          this.osTypeOptions = mappedOptions;
        } else if (osType && !brandName) {
          this.brandNameOptions = mappedOptions;
        } else if (osType && brandName) {
          this.laptopTypeOptions = mappedOptions;
        }

        this.cdr.markForCheck();
      } else {
        console.warn('No data received from accessories API');
      }
    } catch (error) {
      console.error('Error fetching accessories details:', error);
    }
  }

  onOsTypeChange(event: any): void {
    const selectedOsType = event?.value || event;

    // Clear dependent fields
    this.form.patchValue({
      brandName: '',
      laptopType: '',
    });
    this.brandNameOptions = [];
    this.laptopTypeOptions = [];

    if (selectedOsType) {
      this.fetchAccessoriesDetails(selectedOsType, '', 1);
    }
  }

  onBrandNameChange(event: any): void {
    const selectedBrandName = event?.value || event;
    const selectedOsType = this.form.get('osType')?.value;
    // Clear laptop type field
    this.form.patchValue({
      laptopType: '',
    });
    this.laptopTypeOptions = [];

    if (selectedOsType && selectedBrandName) {
      this.fetchAccessoriesDetails(selectedOsType, selectedBrandName, 1);
    } else {
      console.warn('Missing OS Type or Brand Name:', {
        selectedOsType,
        selectedBrandName,
      });
    }
  }

  private formatDate(date: any): string {
    if (!date) {
      return '';
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}
